import { yupResolver } from '@hookform/resolvers/yup'
import { ChangeEvent, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import * as yup from 'yup'

import {
  FestivalGeneralSettings,
  FestivalModulesManager,
  FestivalOtherModules,
} from '../../../common/models'
import { MessageBox } from '../messages/MessageBox'
import { DateTimePicker } from '@/components/ui/date-time-picker'
import { formatDateTime } from '@/core/constants/times'
import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox, Input } from '@/components/ui'
import { Save, XCircle } from 'lucide-react'
import FormGroup from '@/core/components/FormGroup'

interface IFestivalGeneralSettings {
  name?: string
  location?: boolean
  locationName?: string
  localeTime?: Date
  latitude?: string
  longitude?: string
  festivalCounter?: boolean
  logoPNG?: string
  logoSVG?: string
}

interface IModules {
  getTickets?: boolean
  artists?: boolean
  nrArtistsDisplayed?: number
  newsletter?: boolean
  news?: boolean
  nrNewsDisplayed?: number
  stages?: boolean
  appPromo?: boolean
  appSimulator?: string
  promoApps?: boolean
  videoGallery?: boolean
  instagramWidget?: boolean
  nrPostsDisplayed?: number
  ourFriendsWidget?: boolean
  footerCopyright?: string
}

interface IOtherModules {
  topUp?: boolean
  redeemTickets?: boolean
  redeemTopup?: boolean
  checkIn?: boolean
  memories?: boolean
}

export const FestivalSettingsPage: React.FC = () => {
  const [isUploadingGeneralSettings, setIsUploadingGeneralSettings] =
    useState<boolean>(false)
  const [isUploadingModules, setIsUploadingModules] = useState<boolean>(false)
  const [invalidPhoto, setInvalidPhoto] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const [generalSettings, setGeneralSettings] =
    useState<FestivalGeneralSettings>({
      id: '',
      name: '',
      location: false,
      locationName: '',
      localeTime: new Date(),
      latitude: '',
      longitude: '',
      festivalCounter: false,
      logoPNG: '',
      logoSVG: '',
    })
  const [modules, setModules] = useState<FestivalModulesManager>({
    getTickets: false,
    artists: false,
    nrArtistsDisplayed: 0,
    newsletter: false,
    news: false,
    nrNewsDisplayed: 0,
    stages: false,
    appPromo: false,
    appSimulator: '',
    promoApps: false,
    videoGallery: false,
    instagramWidget: false,
    nrPostsDisplayed: 0,
    ourFriendsWidget: false,
    footerCopyright: '',
  })
  const [otherModules, setOtherModules] = useState<FestivalOtherModules>({
    topUp: false,
    redeemTickets: false,
    redeemTopup: false,
    checkIn: false,
    memories: false,
  })

  const navigate = useNavigate()
  const { editionId } = useParams()

  const labelStyle = {
    fontSize: '16px',
    fontFamily: 'Helvetica Neue',
    fontWeight: '700',
    lineHeight: '150%',
    fontStyle: 'normal',
  }

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        // const response = await http.get<IUserProfile>('/web/users/profile') make api call to get data
        const data: FestivalGeneralSettings = {
          id: '',
          name: '',
          location: false,
          locationName: '',
          localeTime: new Date(),
          latitude: '',
          longitude: '',
          festivalCounter: false,
          logoPNG: '',
          logoSVG: '',
        }
        setGeneralSettings(data)
        setValueGeneralSettings('name', generalSettings.name)
        setValueGeneralSettings('location', generalSettings.location)
        setValueGeneralSettings('locationName', generalSettings.locationName)
        setValueGeneralSettings('localeTime', generalSettings.localeTime)
        setValueGeneralSettings('latitude', generalSettings.latitude)
        setValueGeneralSettings('longitude', generalSettings.longitude)
        setValueGeneralSettings('festivalCounter', generalSettings.festivalCounter)
        setValueGeneralSettings('logoPNG', generalSettings.logoPNG)
        setValueGeneralSettings('logoSVG', generalSettings.logoSVG)

        const data2: FestivalModulesManager = {
          getTickets: false,
          artists: false,
          nrArtistsDisplayed: 0,
          newsletter: false,
          news: false,
          nrNewsDisplayed: 0,
          stages: false,
          appPromo: false,
          appSimulator: '',
          promoApps: false,
          videoGallery: false,
          instagramWidget: false,
          nrPostsDisplayed: 0,
          ourFriendsWidget: false,
          footerCopyright: ''
        }
        setModules(data2)
        setValueModules('getTickets', modules.getTickets)
        setValueModules('artists', modules.artists)
        setValueModules('nrArtistsDisplayed', modules.nrArtistsDisplayed)
        setValueModules('newsletter', modules.newsletter)
        setValueModules('news', modules.news)
        setValueModules('nrNewsDisplayed', modules.nrNewsDisplayed)
        setValueModules('stages', modules.stages)
        setValueModules('appPromo', modules.appPromo)
        setValueModules('appSimulator', modules.appSimulator)
        setValueModules('promoApps', modules.promoApps)
        setValueModules('videoGallery', modules.videoGallery)
        setValueModules('instagramWidget', modules.instagramWidget)
        setValueModules('nrPostsDisplayed', modules.nrPostsDisplayed)
        setValueModules('ourFriendsWidget', modules.ourFriendsWidget)
        setValueModules('footerCopyright', modules.footerCopyright)

        const data3: FestivalOtherModules = {
          topUp: false,
          redeemTickets: false,
          redeemTopup: false,
          checkIn: false,
          memories: false,
        }

        setOtherModules(data3)
        setValueOtherModules('checkIn', otherModules.checkIn)
        setValueOtherModules('memories', otherModules.memories)
        setValueOtherModules('redeemTickets', otherModules.redeemTickets)
        setValueOtherModules('redeemTopup', otherModules.redeemTopup)
        setValueOtherModules('topUp', otherModules.topUp)
      } catch (e) {
        console.error(e)
      }
    })()
  }, [])

  const schemaModules = yup.object().shape({
    getTickets: yup.boolean(),
    artists: yup.boolean(),
    nrArtistsDisplayed: yup.number(),
    newsletter: yup.boolean(),
    news: yup.boolean(),
    nrNewsDisplayed: yup.number(),
    stages: yup.boolean(),
    appPromo: yup.boolean(),
    appSimulator: yup.string(),
    promoApps: yup.boolean(),
    videoGallery: yup.boolean(),
    instagramWidget: yup.boolean(),
    nrPostsDisplayed: yup.number(),
    ourFriendsWidget: yup.boolean(),
    footerCopyright: yup.string(),
  })

  const {
    handleSubmit: handleSubmitModules,
    register: registerModules,
    formState: { errors: errorsModules },
    setValue: setValueModules,
    watch: watchModules,
  } = useForm<IModules>({
    mode: 'onChange',
    resolver: yupResolver<IModules>(schemaModules),
    defaultValues: {
      getTickets: false,
      artists: false,
      nrArtistsDisplayed: 0,
      newsletter: false,
      news: false,
      nrNewsDisplayed: 0,
      stages: false,
      appPromo: false,
      appSimulator: '',
      promoApps: false,
      videoGallery: false,
      instagramWidget: false,
      nrPostsDisplayed: 0,
      ourFriendsWidget: false,
      footerCopyright: '',
    },
  })

  const submitModules = async (values: IModules) => {
    console.log(values) //TODO replace with the submit api call
  }

  const handleCancelModules = async () => {
    setValueGeneralSettings('name', generalSettings.name)
    setValueGeneralSettings('location', generalSettings.location)
    setValueGeneralSettings('locationName', generalSettings.locationName)
    setValueGeneralSettings('localeTime', generalSettings.localeTime)
    setValueGeneralSettings('latitude', generalSettings.latitude)
    setValueGeneralSettings('longitude', generalSettings.longitude)
    setValueGeneralSettings('festivalCounter', generalSettings.festivalCounter)
    setValueGeneralSettings('logoPNG', generalSettings.logoPNG)
    setValueGeneralSettings('logoSVG', generalSettings.logoSVG)
  }

  const uploadImageModules = async (file: File) => {
    setIsUploadingModules(true)
    try {
      console.log(file)
      // const { data } = await uploadAdminsPhoto(editionId, file, "ImageCOnfig")
      // return data.photoUrl
    } catch (err) {
      console.log(err)
      return ''
    } finally {
      setIsUploadingModules(false)
    }
  }

  const schemaGeneralSettings = yup.object().shape({
    name: yup.string(),
    location: yup.boolean(),
    locationName: yup.string(),
    localeTime: yup.date(),
    latitude: yup.string(),
    longitude: yup.string(),
    festivalCounter: yup.boolean(),
    logoPNG: yup.string(),
    logoSVG: yup.string(),
  })

  const {
    handleSubmit: handleSubmitGeneralSettings,
    register: registerGeneralSettings,
    formState: { errors: errorsGeneralSettings },
    setValue: setValueGeneralSettings,
    watch: watchGeneralSettings,
    control: controlGeneralSettings,
  } = useForm<IFestivalGeneralSettings>({
    mode: 'onChange',
    resolver: yupResolver<IFestivalGeneralSettings>(schemaGeneralSettings),
    defaultValues: {
      name: '',
      location: false,
      locationName: '',
      localeTime: new Date(),
      latitude: '',
      longitude: '',
      festivalCounter: false,
      logoPNG: '',
      logoSVG: '',
    },
  })

  const submitGeneralSettings = async (values: IFestivalGeneralSettings) => {
    console.log(values) //TODO replace with the submit api call
  }

  const handleCancelGeneralSettings = async () => {
    setValueGeneralSettings('name', generalSettings.name)
    setValueGeneralSettings('location', generalSettings.location)
    setValueGeneralSettings('locationName', generalSettings.locationName)
    setValueGeneralSettings('localeTime', generalSettings.localeTime)
    setValueGeneralSettings('latitude', generalSettings.latitude)
    setValueGeneralSettings('longitude', generalSettings.longitude)
    setValueGeneralSettings('festivalCounter', generalSettings.festivalCounter)
    setValueGeneralSettings('logoPNG', generalSettings.logoPNG)
    setValueGeneralSettings('logoSVG', generalSettings.logoSVG)
  }

  const uploadImageGeneralSettings = async (file: File) => {
    setIsUploadingGeneralSettings(true)
    try {
      console.log(file)
      // const { data } = await uploadAdminsPhoto(editionId, file)
      // return data.photoUrl
    } catch (err) {
      console.log(err)
      return ''
    } finally {
      setIsUploadingGeneralSettings(false)
    }
  }

  const schemaOtherModules = yup.object().shape({
    topUp: yup.boolean(),
    redeemTickets: yup.boolean(),
    redeemTopup: yup.boolean(),
    checkIn: yup.boolean(),
    memories: yup.boolean(),
  })

  const {
    handleSubmit: handleSubmitOtherModules,
    register: registerOtherModules,
    formState: { errors: errorsOtherModules },
    setValue: setValueOtherModules,
    watch: watchOtherModules,
  } = useForm<IOtherModules>({
    mode: 'onChange',
    resolver: yupResolver<IOtherModules>(schemaOtherModules),
    defaultValues: {
      topUp: false,
      redeemTickets: false,
      redeemTopup: false,
      checkIn: false,
      memories: false,
    },
  })

  const submitOtherModules = async (values: IOtherModules) => {
    console.log(values) //TODO replace with the submit api call
  }

  const handleCancelOtherModules = async () => {
    setValueOtherModules('checkIn', otherModules.checkIn)
    setValueOtherModules('memories', otherModules.memories)
    setValueOtherModules('redeemTickets', otherModules.redeemTickets)
    setValueOtherModules('redeemTopup', otherModules.redeemTopup)
    setValueOtherModules('topUp', otherModules.topUp)
  }

  return (
    <>
      <Header pageTitle="Festival Settings" />
      <Card className="!bg-monochrome4-60 p-5 backdrop-blur-lg max-h-[calc(100vh-150px)] overflow-y-auto">
        <div className="flex items-center justify-between gap-4 mb-6">
          <PageTitle
            title={`${editionId}`}
            backButton
            backButtonFunction={() => navigate(-1)}
          />
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                handleCancelGeneralSettings()
                handleCancelModules()
                handleCancelOtherModules()
              }}
            >
              <XCircle className="w-4 h-4" />
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                handleSubmitGeneralSettings(submitGeneralSettings)
                handleSubmitModules(submitModules)
                handleSubmitOtherModules(submitOtherModules)
              }}
            >
              <Save className="w-4 h-4" />
              Save
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-5">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-title-s text-accent-1">General Settings</h4>
            </div>
            <div className="border border-monochrome-120 rounded-2xl">
              <div className="p-3">
                <form
                  id="settings-email-form"
                  onSubmit={handleSubmitGeneralSettings(submitGeneralSettings)}
                >
                  <div className="flex flex-col items-start gap-3">
                    <FormGroup label="Festival Name">
                      <Input
                        isInvalid={!!errorsGeneralSettings?.name?.message}
                        {...registerGeneralSettings('name')}
                        type="text"
                        placeholder="Enter Name"
                      />
                    </FormGroup>
                    <FormGroup label="Location">
                      <Checkbox
                        checked={watchGeneralSettings('location')}
                        onCheckedChange={(checked) => {
                          setValueGeneralSettings('location', Boolean(checked))
                        }}
                        {...registerGeneralSettings('location')}
                      />
                    </FormGroup>
                    <FormGroup label="Location Name">
                      <Input
                        isInvalid={
                          !!errorsGeneralSettings?.locationName?.message
                        }
                        {...registerGeneralSettings('locationName')}
                        type="text"
                        placeholder="Enter Location Name"
                      />
                    </FormGroup>
                    <FormGroup label="Local Time">
                      <Controller
                        control={controlGeneralSettings}
                        name="localeTime"
                        render={({ field }) => (
                          <DateTimePicker
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Enter Locale Time"
                            hasError={
                              !!errorsGeneralSettings?.localeTime?.message
                            }
                          />
                        )}
                      />
                    </FormGroup>
                    <FormGroup label="Latitude">
                      <Input
                        isInvalid={!!errorsGeneralSettings?.latitude?.message}
                        {...registerGeneralSettings('latitude')}
                        type="text"
                        placeholder="Enter Copyright message"
                      />
                    </FormGroup>
                    <FormGroup label="Longitude">
                      <Input
                        isInvalid={!!errorsGeneralSettings?.longitude?.message}
                        {...registerGeneralSettings('longitude')}
                        type="text"
                        placeholder="Enter Longitude"
                      />
                    </FormGroup>
                    <FormGroup label="Festival Counter">
                      <Checkbox
                        checked={watchGeneralSettings('festivalCounter')}
                        onCheckedChange={(checked) => {
                          setValueGeneralSettings(
                            'festivalCounter',
                            Boolean(checked),
                          )
                        }}
                        {...registerGeneralSettings('festivalCounter')}
                      />
                    </FormGroup>
                    <FormGroup label="Logo Upload PNG (145X40)">
                      <Input
                        isInvalid={!!errorsGeneralSettings?.logoPNG?.message}
                        {...registerGeneralSettings('logoPNG')}
                        type="file"
                        accept="image/*"
                      />
                    </FormGroup>
                    <FormGroup label="Logo Upload SVG">
                      <Input
                        isInvalid={!!errorsGeneralSettings?.logoSVG?.message}
                        {...registerGeneralSettings('logoSVG')}
                        type="file"
                        accept="image/*"
                        onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                          const file = e?.target?.files?.[0]
                          if (file) {
                            if (file.size > 1_000_000) {
                              setInvalidPhoto(true)
                              setIsError(true)
                              setShowMessageBox(true)
                              setMessage('The logo is too large (1 MB)')
                              return
                            }
                            const url = await uploadImageGeneralSettings(file)
                            console.log(url)

                            if (url) {
                              setValueGeneralSettings(
                                'logoSVG',
                                url as string,
                                {
                                  shouldValidate: true,
                                },
                              )
                            }
                          }
                        }}
                      />
                    </FormGroup>
                  </div>
                </form>
              </div>
              <div className="flex justify-end gap-2 p-4 border-t border-monochrome-120">
                <Button
                  onClick={handleCancelGeneralSettings}
                  variant="secondary"
                  data-dismiss="modal"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  form="settings-email-form"
                  type="submit"
                  disabled={isUploadingGeneralSettings}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-title-s text-accent-1">Modules Management</h4>
            </div>
            <div className="border border-monochrome-120 rounded-2xl">
              <div className="p-3">
                <form
                  id="settings-email-form"
                  onSubmit={handleSubmitModules(submitModules)}
                >
                  <div className="flex flex-col items-start gap-3">
                    <FormGroup
                      label="Get Tickets"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                    >
                      <Checkbox
                        checked={watchModules('getTickets')}
                        onCheckedChange={(checked) => {
                          setValueModules('getTickets', Boolean(checked))
                        }}
                        {...registerModules('getTickets')}
                      />
                    </FormGroup>
                    <FormGroup
                      label="Artists"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                    >
                      <Checkbox
                        checked={watchModules('artists')}
                        onCheckedChange={(checked) => {
                          setValueModules('artists', Boolean(checked))
                        }}
                        {...registerModules('artists')}
                      />
                    </FormGroup>
                    <FormGroup label="Number of Artists to Display on Homepage">
                      <Input
                        isInvalid={!!errorsModules?.nrArtistsDisplayed?.message}
                        {...registerModules('nrArtistsDisplayed')}
                        type="number"
                        placeholder="Enter number of artists to display"
                      />
                    </FormGroup>
                    <FormGroup
                      label="Newsletter"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                    >
                      <Checkbox
                        checked={watchModules('newsletter')}
                        onCheckedChange={(checked) => {
                          setValueModules('newsletter', Boolean(checked))
                        }}
                        {...registerModules('newsletter')}
                      />
                    </FormGroup>
                    <FormGroup
                      label="News"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                    >
                      <Checkbox
                        checked={watchModules('news')}
                        onCheckedChange={(checked) => {
                          setValueModules('news', Boolean(checked))
                        }}
                        {...registerModules('news')}
                      />
                    </FormGroup>
                    <FormGroup label="Number of News to Display on Homepage">
                      <Input
                        isInvalid={!!errorsModules?.nrNewsDisplayed?.message}
                        {...registerModules('nrNewsDisplayed')}
                        type="number"
                        placeholder="Enter number of news to display"
                      />
                    </FormGroup>
                    <FormGroup
                      label="Stages"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                    >
                      <Checkbox
                        checked={watchModules('stages')}
                        onCheckedChange={(checked) => {
                          setValueModules('stages', Boolean(checked))
                        }}
                        {...registerModules('stages')}
                      />
                    </FormGroup>
                    <FormGroup
                      label="App Promo"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                    >
                      <Checkbox
                        checked={watchModules('appPromo')}
                        onCheckedChange={(checked) => {
                          setValueModules('appPromo', Boolean(checked))
                        }}
                        {...registerModules('appPromo')}
                      />
                    </FormGroup>
                    <FormGroup label="App Simulator Image (1000X533)">
                      <Input
                        multiple={false}
                        accept="image/*"
                        isInvalid={
                          !!errorsModules?.appSimulator?.message || invalidPhoto
                        }
                        name="photo"
                        onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                          const file = e?.target?.files?.[0]
                          if (file) {
                            if (file.size > 1_000_000) {
                              setInvalidPhoto(true)
                              setIsError(true)
                              setShowMessageBox(true)
                              setMessage('The photo is too large (1 MB)')
                              return
                            }
                            const url = await uploadImageModules(file)
                            console.log(url)

                            if (url) {
                              setValueModules('appSimulator', url as string, {
                                shouldValidate: true,
                              })
                            }
                          }
                        }}
                        type="file"
                      />
                    </FormGroup>
                    <FormGroup
                      label="Promo Apps"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                    >
                      <Checkbox
                        checked={watchModules('promoApps')}
                        onCheckedChange={(checked) => {
                          setValueModules('promoApps', Boolean(checked))
                        }}
                        {...registerModules('promoApps')}
                      />
                    </FormGroup>
                    <FormGroup
                      label="Video Gallery"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                    >
                      <Checkbox
                        checked={watchModules('videoGallery')}
                        onCheckedChange={(checked) => {
                          setValueModules('videoGallery', Boolean(checked))
                        }}
                        {...registerModules('videoGallery')}
                      />
                    </FormGroup>
                    <FormGroup
                      label="Instagram Widget"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                    >
                      <Checkbox
                        checked={watchModules('instagramWidget')}
                        onCheckedChange={(checked) => {
                          setValueModules('instagramWidget', Boolean(checked))
                        }}
                        {...registerModules('instagramWidget')}
                      />
                    </FormGroup>
                    <FormGroup label="Number Posts to Display">
                      <Input
                        isInvalid={!!errorsModules?.nrPostsDisplayed?.message}
                        {...registerModules('nrPostsDisplayed')}
                        type="number"
                        placeholder="Enter number of posts to display"
                      />
                    </FormGroup>
                    <FormGroup
                      label="Our Friends Widget"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                    >
                      <Checkbox
                        checked={watchModules('ourFriendsWidget')}
                        onCheckedChange={(checked) => {
                          setValueModules('ourFriendsWidget', Boolean(checked))
                        }}
                        {...registerModules('ourFriendsWidget')}
                      />
                    </FormGroup>
                    <FormGroup label="Footer Copyright">
                      <Input
                        isInvalid={!!errorsModules?.footerCopyright?.message}
                        {...registerModules('footerCopyright')}
                        type="text"
                        placeholder="Enter Copyright Message"
                      />
                    </FormGroup>
                  </div>
                </form>
              </div>
              <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120">
                <Button onClick={handleCancelModules} variant="secondary">
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  form="settings-email-form"
                  type="submit"
                  disabled={isUploadingModules}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-title-s text-accent-1">Other Modules</h4>
            </div>
            <div className="border border-monochrome-120 rounded-2xl">
              <div className="p-3">
                <form
                  id="settings-email-form"
                  onSubmit={handleSubmitOtherModules(submitOtherModules)}
                >
                  <div className="flex flex-col items-start gap-3">
                    <FormGroup
                      label="Top Up"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                    >
                      <Checkbox
                        checked={watchOtherModules('topUp')}
                        onCheckedChange={(checked) => {
                          setValueOtherModules('topUp', Boolean(checked))
                        }}
                        {...registerOtherModules('topUp')}
                      />
                    </FormGroup>
                    <FormGroup
                      label="Reedeem Tickets"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                    >
                      <Checkbox
                        checked={watchOtherModules('redeemTickets')}
                        onCheckedChange={(checked) => {
                          setValueOtherModules(
                            'redeemTickets',
                            Boolean(checked),
                          )
                        }}
                        {...registerOtherModules('redeemTickets')}
                      />
                    </FormGroup>
                    <FormGroup
                      label="Reedeem Topup"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                    >
                      <Checkbox
                        checked={watchOtherModules('redeemTopup')}
                        onCheckedChange={(checked) => {
                          setValueOtherModules('redeemTopup', Boolean(checked))
                        }}
                        {...registerOtherModules('redeemTopup')}
                      />
                    </FormGroup>
                    <FormGroup
                      label="Check-in"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                    >
                      <Checkbox
                        checked={watchOtherModules('checkIn')}
                        onCheckedChange={(checked) => {
                          setValueOtherModules('checkIn', Boolean(checked))
                        }}
                        {...registerOtherModules('checkIn')}
                      />
                    </FormGroup>
                    <FormGroup
                      label="Memories"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                    >
                      <Checkbox
                        checked={watchOtherModules('memories')}
                        onCheckedChange={(checked) => {
                          setValueOtherModules('memories', Boolean(checked))
                        }}
                        {...registerOtherModules('memories')}
                      />
                    </FormGroup>
                  </div>
                </form>
              </div>
              <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120">
                <Button onClick={handleCancelOtherModules} variant="secondary">
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  form="settings-email-form"
                  type="submit"
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
